import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useQuery } from 'react-apollo';
import moment from 'moment';

import { QUERY_PROPERTY_DETAILS, QUERY_PROPERTY_ROOMS } from './gql';
import toastMessages from './toasts';
import withBooking, { withBookingPropType } from '../../../hocs/withBooking';
import withAnalytics, { withAnalyticsPropType } from '../../../hocs/withAnalytics';
import withAuth, { ORG_PERMISSIONS, withAuthPropType } from '../../../hocs/withAuth';
import withRouting, { withRoutingPropType } from '../../../hocs/withRouting';
import PropertyAvailability from '../../../constants/enums/PropertyAvailability';
import { convertPropertyDateToUTCTime, convertUTCDateToPropertyTime } from '../../../helpers/timeZoneHelper';
import { PropertySearchContext } from '../../../hooks/usePropertySearchContext';

const buildQueryParams = (checkin, checkout, numRooms, bookingBypassReasonId, propertyTimeZone) => {
  let queryString = `?checkin=${checkin}&checkout=${checkout}`;
  if (numRooms) {
    queryString += `&numRooms=${numRooms}`;
  }
  if (bookingBypassReasonId) {
    queryString += `&bookingBypassReasonId=${bookingBypassReasonId}`;
  }
  if (propertyTimeZone) {
    queryString += `&propertyTimeZone=${propertyTimeZone}`;
  }
  return queryString;
};

const mapNightlyRates = (checkin, checkout, price, value) => {
  const checkinDate = moment(checkin);
  const checkoutDate = moment(checkout);
  const nightlyRates = [];
  while (checkinDate.isBefore(checkoutDate)) {
    nightlyRates.push({ date: checkinDate.format('YYYY-MM-DD'), price, value });
    checkinDate.add(1, 'd');
  }

  return nightlyRates;
};

// --- Helper Functions for Rate Processing ---

// Calculates the base rate object based on averageRateAfterCalculations and callForPricing
const calculateBaseRate = (averageRateAfterCalculations, callForPricing, curRoomRate) => {
  let formatted = 'PLEASE CALL FOR ADDITIONAL INFORMATION';
  let name = '';
  let value = null;
  let adminValues = curRoomRate?.adminValues;

  if (averageRateAfterCalculations) {
    formatted =
      callForPricing || !averageRateAfterCalculations.formatted ? formatted : averageRateAfterCalculations.formatted;
    name = callForPricing ? name : averageRateAfterCalculations.name || name;
    value = callForPricing ? value : averageRateAfterCalculations.value;
    adminValues = averageRateAfterCalculations;
  }

  return {
    formatted: formatted ?? curRoomRate?.formatted,
    name: name ?? curRoomRate?.name,
    value: value ?? curRoomRate?.value,
    adminValues: adminValues ?? curRoomRate?.adminValues,
    callForPricing: callForPricing ?? curRoomRate?.callForPricing
  };
};

// Calculates the strikeout rate string if applicable
const calculateStrikeoutRate = (fetchedStrikeout, baseRate) => {
  if (
    fetchedStrikeout &&
    baseRate.value != null &&
    fetchedStrikeout.value > baseRate.value &&
    fetchedStrikeout.type !== 'LNR'
  ) {
    return fetchedStrikeout.formatted;
  }
  return null;
};

// Applies LNR rate override if conditions are met
const applyRateOverride = (fetchedStrikeout, ceilingRate, baseRate, callForPricing) => {
  let isRateOverrideActive = false;
  const rateOverride = {};

  if (fetchedStrikeout && baseRate.value != null && fetchedStrikeout.type === 'LNR') {
    const isCeilingBlocked = ceilingRate && ceilingRate.value <= fetchedStrikeout.value;
    if (!isCeilingBlocked) {
      rateOverride.formatted = fetchedStrikeout.formatted;
      rateOverride.value = fetchedStrikeout.value;
      rateOverride.adminValues = {
        ...(baseRate.adminValues || {}),
        value: fetchedStrikeout.value,
        formatted: fetchedStrikeout.formatted
      };
      rateOverride.name = fetchedStrikeout.name;
      rateOverride.callForPricing = callForPricing ?? baseRate?.callForPricing;
      isRateOverrideActive = true;
    }
  }
  return { rateOverride, isRateOverrideActive };
};

// Formats nightly rates, handling overrides and timezones
const formatNightlyRates = (
  roomDataNightlyRates,
  isRateOverrideActive,
  rateOverride,
  checkin,
  checkout,
  propertyTimeZone
) => {
  let finalNightlyRates = roomDataNightlyRates;

  if (isRateOverrideActive) {
    finalNightlyRates =
      checkin && checkout ? mapNightlyRates(checkin, checkout, rateOverride.formatted, rateOverride.value) : [];
  }

  if (finalNightlyRates && propertyTimeZone) {
    return finalNightlyRates
      .map(nightlyRate => ({
        ...nightlyRate,
        date: nightlyRate.date
          ? convertUTCDateToPropertyTime({
              dateInUTC: nightlyRate.date,
              propertyTimeZone
            })
          : null
      }))
      .filter(Boolean); // Ensure null dates don't cause issues if map returns null
  }
  return finalNightlyRates || []; // Ensure it always returns an array
};

// Processes room rate data using helper functions
const processRoomRateData = (roomData, curRoom, callForPricing, checkin, checkout, propertyTimeZone) => {
  const baseRate = calculateBaseRate(roomData.averageRateAfterCalculations, callForPricing, curRoom.rate);
  const fetchedStrikeout = get(roomData, 'strikeoutRate');
  const strikeoutRate = calculateStrikeoutRate(fetchedStrikeout, baseRate);
  const ceilingRate = get(roomData, 'ceilingRate');
  const { rateOverride, isRateOverrideActive } = applyRateOverride(
    fetchedStrikeout,
    ceilingRate,
    baseRate,
    callForPricing
  );

  const nightlyRates = formatNightlyRates(
    roomData.nightlyRates,
    isRateOverrideActive,
    rateOverride,
    checkin,
    checkout,
    propertyTimeZone
  );

  return {
    ...curRoom,
    rate: isRateOverrideActive ? rateOverride : baseRate,
    ceilingRate,
    strikeoutRate,
    callForPricing: roomData.callForPricing ?? curRoom.callForPricing,
    nightlyRates,
    availability: PropertyAvailability[roomData.availability] || curRoom.availability,
    numRoomsAvail: roomData.numRoomsAvail,
    averageRateAfterCalculations: roomData.averageRateAfterCalculations
  };
};

// Define the component that uses the hooks internally
const BookNowContainerInternal = ({
  slug,
  analytics,
  booking,
  children,
  auth,
  router,
  showError,
  propertyTimeZone
}) => {
  const { searchCriteria, setSearchCriteria } = useContext(PropertySearchContext);
  const { startDate: checkin, endDate: checkout, roomCount: numRoomsString } = searchCriteria;

  // --- State for data derived from queries ---
  const [propertyDetails, setPropertyDetails] = useState({
    id: undefined,
    name: undefined,
    timeZone: undefined,
    connected: undefined,
    currencyType: 'CAD' // Default
  });
  const [initialRooms, setInitialRooms] = useState([]);
  const [finalRooms, setFinalRooms] = useState([]);

  // --- Date Conversion Logic ---
  const { checkinUTC, checkoutUTC } = useMemo(() => {
    let cInUTC = checkin;
    let cOutUTC = checkout;
    if (propertyTimeZone && checkin && checkout) {
      cInUTC = convertPropertyDateToUTCTime(checkin, propertyTimeZone);
      cOutUTC = convertPropertyDateToUTCTime(checkout, propertyTimeZone);
    }
    return { checkinUTC: cInUTC, checkoutUTC: cOutUTC };
  }, [checkin, checkout, propertyTimeZone]);

  // --- Query 1: Property Details ---
  const { loading: detailsLoading, error: detailsError } = useQuery(QUERY_PROPERTY_DETAILS, {
    variables: {
      slug,
      // Use unconverted dates initially, timezone conversion happens before rates query
      dates: {
        checkin: checkinUTC,
        checkout: checkoutUTC
        // Note: Ideally, this first query might not need dates if only fetching static details + timezone
      }
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !slug, // Skip if slug is not available
    onCompleted: data => {
      const propertyData = get(data, 'property');
      if (propertyData) {
        setPropertyDetails({
          id: get(propertyData, 'id'),
          name: get(propertyData, 'name'),
          timeZone: get(propertyData, 'timeZone'),
          connected: get(propertyData, 'connected'),
          currencyType: get(propertyData, 'locale.currencyName', 'CAD')
        });

        const timeZone = get(propertyData, 'timeZone');
        const callForPricing = get(propertyData, 'callForPricing');
        // Process initial rooms structure (without rates)
        const baseRooms = get(propertyData, 'rooms', [])
          .map(room => {
            const { averageRateAfterCalculations } = room; // May exist but could be from stale cache
            let formatted = 'PLEASE CALL FOR ADDITIONAL INFORMATION';
            let name = '';
            let value = null;
            let adminValues = room.rate; // Default to original rate obj

            if (averageRateAfterCalculations) {
              // Use initial values cautiously, they might be overridden by rates query
              formatted =
                callForPricing || !averageRateAfterCalculations.formatted
                  ? formatted
                  : averageRateAfterCalculations.formatted;
              name = callForPricing ? name : averageRateAfterCalculations.name || name;
              value = callForPricing ? value : averageRateAfterCalculations.value;
              adminValues = averageRateAfterCalculations;
            }

            const rate = {
              formatted,
              name,
              value,
              adminValues,
              callForPricing
            };

            let strikeoutRate = null;
            if (room.strikeoutRate && rate.value != null && room.strikeoutRate.value > rate.value) {
              strikeoutRate = room.strikeoutRate.formatted;
            }

            let { nightlyRates } = room;
            if (nightlyRates && timeZone) {
              nightlyRates = nightlyRates.map(nightlyRate => ({
                ...nightlyRate,
                date: nightlyRate.date
                  ? convertUTCDateToPropertyTime({
                      dateInUTC: nightlyRate.date,
                      propertyTimeZone: timeZone,
                      fullISOFormat: true
                    })
                  : null
              }));
            }

            return {
              available: room.available,
              availableRooms: room.availableRooms,
              description: room.description,
              guestsPerRoom: room.guestsPerRoom,
              id: room.id ? room.id.toString() : undefined,
              name: room.name,
              rate, // Initial rate structure
              ceilingRate: room?.ceilingRate,
              strikeoutRate,
              callForPricing: room.callForPricing,
              nightlyRates,
              availability: PropertyAvailability[room.availability] || PropertyAvailability.AVAILABLE
            };
          })
          .filter(room => room.id)
          .sort((roomA, roomB) => (roomA.rate.value ?? Infinity) - (roomB.rate.value ?? Infinity));
        setInitialRooms(baseRooms);
        setFinalRooms(baseRooms); // Initialize finalRooms with base structure
      }
    }
  });

  const skipRatesQuery =
    detailsLoading ||
    !propertyTimeZone ||
    !checkinUTC ||
    !checkoutUTC ||
    initialRooms.length === 0 ||
    !propertyDetails.connected;

  const { loading: ratesLoading, error: ratesError } = useQuery(QUERY_PROPERTY_ROOMS, {
    variables: {
      slug,
      dates: {
        checkin: checkinUTC,
        checkout: checkoutUTC
      }
    },
    fetchPolicy: 'cache-and-network',
    skip: skipRatesQuery,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const propertyRateData = get(data, 'property');
      const callForPricing = get(propertyRateData, 'callForPricing');
      const rateRooms = get(propertyRateData, 'rooms', []);

      if (rateRooms.length > 0) {
        // Merge rates data with initialRooms structure using the refactored logic
        const updatedRooms = initialRooms
          .map(curRoom => {
            if (!curRoom?.id) return curRoom; // Skip invalid rooms early
            const roomData = rateRooms.find(r => r.id && r.id.toString() === curRoom.id);

            if (roomData) {
              return processRoomRateData(roomData, curRoom, callForPricing, checkin, checkout, propertyTimeZone);
            }
            return curRoom;
          })
          .filter(Boolean);

        setFinalRooms(updatedRooms);
      } else {
        setFinalRooms(initialRooms);
      }
    }
  });

  // --- Component Logic using Context and Query Results ---
  const numRooms = useMemo(() => parseInt(numRoomsString, 10) || 0, [numRoomsString]);
  const nights = useMemo(() => (checkin && checkout ? moment(checkout).diff(checkin, 'days') : 0), [checkin, checkout]);

  const [roomInputs, setRoomInputs] = useState({});
  const [isBlockBooking, setIsBlockBooking] = useState(false);
  const [showBlockBookingToggle, setShowBlockBookingToggle] = useState(false);

  const bookingBypassReasonId = useMemo(() => parseInt(router.query.bookingBypassReasonId, 10) || undefined, [
    router.query.bookingBypassReasonId
  ]);

  // Effect for checking admin permissions on mount
  useEffect(() => {
    const adminToken = get(auth, 'me.adminToken');
    const permissions = get(auth, 'me.activeOrganization.permissions');
    if (adminToken && permissions && permissions.includes(ORG_PERMISSIONS.CUS_CREATE_BLOCK_BOOKING)) {
      setShowBlockBookingToggle(true);
    }
  }, [auth]);

  // Effect for resetting room inputs when finalRooms data changes
  useEffect(() => {
    // Only run if not loading rates and finalRooms are available
    if (!ratesLoading && finalRooms && finalRooms.length > 0) {
      setRoomInputs(currentInputs => {
        const newInputs = { ...currentInputs };
        let changed = false;
        if (propertyDetails.connected) {
          finalRooms.forEach(room => {
            if (!room.id) return;
            if ((room.availability === PropertyAvailability.SOLD_OUT || !room.availability) && newInputs[room.id]) {
              if (newInputs[room.id] !== 0) {
                newInputs[room.id] = 0;
                changed = true;
              }
            } else if (newInputs[room.id] === undefined) {
              newInputs[room.id] = 0;
              changed = true;
            }
          });
        }
        return changed ? newInputs : currentInputs;
      });
    }
    // Reset if rooms array itself changes reference
  }, [ratesLoading, finalRooms, propertyDetails.connected]);

  // Effect for updating URL query params (Now depends on context and propertyDetails state)
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !detailsLoading &&
      !detailsError &&
      finalRooms &&
      propertyTimeZone &&
      checkin &&
      checkout
    ) {
      const { asPath } = router;
      const queryAt = asPath.indexOf('?');
      const newPath = queryAt === -1 ? asPath : asPath.substring(0, queryAt);
      const currentQueryParams = buildQueryParams(checkin, checkout, numRooms, bookingBypassReasonId, propertyTimeZone);
      const targetPath = newPath + currentQueryParams;

      if (asPath !== targetPath) {
        const {
          history,
          document: { title }
        } = window;
        history.replaceState(history.state, title, targetPath);
      }
    }
  }, [
    detailsLoading,
    detailsError,
    finalRooms,
    checkin,
    checkout,
    numRooms,
    bookingBypassReasonId,
    propertyTimeZone,
    router
  ]);

  const updateRoomCount = useCallback((id, value) => {
    setRoomInputs(prevInputs => ({
      ...prevInputs,
      [id]: value
    }));
  }, []);

  const getRoomCount = useCallback(roomId => roomInputs[roomId] || 0, [roomInputs]);

  const totalRoomCount = useMemo(
    () =>
      Object.keys(roomInputs).reduce((accumulator, roomId) => {
        const count = roomInputs[roomId] || 0;
        return accumulator + count;
      }, 0),
    [roomInputs]
  );

  const capacity = useMemo(() => {
    if (!finalRooms) return 0;
    return finalRooms.reduce((accumulator, room) => {
      if (!room.id) return accumulator; // Skip rooms without id
      const roomQuantity = roomInputs[room.id] || 0;
      return accumulator + roomQuantity * room.guestsPerRoom;
    }, 0);
  }, [finalRooms, roomInputs]);

  const estimatedCost = useMemo(() => {
    if (!finalRooms) return 0;
    const estimatedTotalPerNight = finalRooms.reduce((accumulator, room) => {
      if (!room.id) return accumulator;
      const count = roomInputs[room.id] || 0;
      let roomRate = get(room, 'rate.value');
      // Handle admin viewing potentially null rates
      if (get(auth, 'me.admin') && roomRate === null) {
        roomRate = get(room, 'rate.adminValues.value');
      }
      // Ensure roomRate is a number
      roomRate = typeof roomRate === 'number' ? roomRate : 0;

      return accumulator + count * roomRate;
    }, 0);

    return estimatedTotalPerNight * nights;
  }, [finalRooms, roomInputs, nights, auth]);

  const onDatesChanged = useCallback(
    ([startDate, endDate]) => {
      if (startDate && endDate) {
        setSearchCriteria({
          type: 'updateDateCriteria',
          payload: {
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD')
          }
        });
      }
    },
    [setSearchCriteria]
  );

  const onNumRoomsChange = useCallback(
    value => {
      const parsedValue = parseInt(value, 10);
      if (!Number.isNaN(parsedValue)) {
        setSearchCriteria({
          type: 'updateRoomCount',
          payload: {
            count: parsedValue
          }
        });
      }
    },
    [setSearchCriteria]
  );

  const toggleBlockBooking = useCallback(() => {
    setIsBlockBooking(prev => !prev);
  }, []);

  const onInitiateBooking = useCallback(() => {
    const { id: propertyId, name: propertyName } = propertyDetails;

    if (!propertyId) {
      return;
    }

    if (totalRoomCount < 1 && !bookingBypassReasonId) {
      return;
    }

    const mutationInputRooms = Object.keys(roomInputs)
      .map(roomId => ({
        quantity: roomInputs[roomId] || 0,
        roomId
      }))
      .filter(room => room.quantity !== 0);

    const isAfterCheckin = moment().isAfter(moment(checkin), 'day');
    if (isAfterCheckin) {
      showError(toastMessages.checkInErrorToast);
      return;
    }

    booking.startPropertyBookingFlow(
      propertyId,
      checkin,
      checkout,
      mutationInputRooms,
      bookingBypassReasonId,
      propertyTimeZone,
      isBlockBooking
    );

    analytics.trackEvent('property-book', {
      sectionName: 'Property Details',
      lodgeID: propertyId,
      lodgeName: propertyName
    });
  }, [
    propertyDetails,
    totalRoomCount,
    bookingBypassReasonId,
    roomInputs,
    checkin,
    checkout,
    showError,
    booking,
    isBlockBooking,
    analytics
  ]);

  const checkBookingPriceMismatch = useCallback(() => {
    const me = get(auth, 'me');
    if (!me || typeof window === 'undefined' || !finalRooms || finalRooms.length === 0) return;

    const { id: propertyId } = propertyDetails;

    const {
      activeOrganization: { name: organizationName },
      emailAddress,
      admin
    } = me;
    const PriceMismatchEnums = {
      PriceMismatch: 'PriceMismatch',
      PDPRoomUnavailability: 'PDPRoomUnavailability',
      SoldOutRooms: 'SoldOutRooms'
    };

    const propertyPricingMismatchStr = window.sessionStorage.getItem('propertyPricingMismatch');
    if (!propertyPricingMismatchStr) return; // Exit early if no item

    let propertyPricingMismatchObj;
    try {
      propertyPricingMismatchObj = JSON.parse(propertyPricingMismatchStr);
    } catch (e) {
      console.error('Error parsing propertyPricingMismatch from sessionStorage', e);
      window.sessionStorage.removeItem('propertyPricingMismatch'); // Clear invalid data
      return;
    }

    if (!propertyPricingMismatchObj || !Object.keys(propertyPricingMismatchObj).length) {
      return;
    }

    const {
      checkin: checkinFromStorage,
      checkout: checkoutFromStorage,
      property: { label = '', priceValue, slug: slugFromStorage, id: idFromStorage, connected, availability } = {}
    } = propertyPricingMismatchObj;

    // Ensure data from storage is valid and matches current context
    if (!slugFromStorage || slugFromStorage !== slug || !idFromStorage || idFromStorage !== propertyId) {
      window.sessionStorage.removeItem('propertyPricingMismatch'); // Clear stale data
      return;
    }

    if (checkinFromStorage !== checkin || checkoutFromStorage !== checkout) return;
    if (priceValue == null || (priceValue != null && availability === PropertyAvailability.SOLD_OUT)) return; // Use != null check

    const getRoomPrices =
      finalRooms.map(room =>
        typeof room.rate.value === 'number' ? room.rate.value : get(room, 'rate.adminValues.value')
      ) || [];
    const roomAvailability = finalRooms.map(room => room.availability) || [];

    const analyticsObject = {
      checkin: checkinFromStorage,
      checkout: checkoutFromStorage,
      propertySlug: slugFromStorage,
      property: idFromStorage,
      connected,
      sectionName: 'Property details',
      eventName: 'trackPriceMismatchEvent',
      organizationName,
      customerEmailAddress: emailAddress,
      propertyName: label,
      mapPagePriceValue: priceValue,
      propertyRoomPrices: JSON.stringify(getRoomPrices)
    };

    const isPriceMismatched = finalRooms.find(room => {
      const roomRateValue = typeof room.rate.value === 'number' ? room.rate.value : get(room, 'rate.adminValues.value');
      // Use == for comparison to handle potential type differences if priceValue comes from storage string
      // eslint-disable-next-line eqeqeq
      return roomRateValue == priceValue;
    });

    if (!isPriceMismatched) {
      const mappedRooms = finalRooms.map(room => ({
        id: room?.id,
        pdpRateType: room?.nightlyRates?.[0]?.rateCategory,
        name: room?.name,
        availability: room?.availability,
        rate: typeof room.rate.value === 'number' ? room.rate.value : get(room, 'rate.adminValues.value')
      }));
      analytics.trackGA4('track-event-pricing', {
        ...analyticsObject,
        priceMismatchType: PriceMismatchEnums.PriceMismatch,
        rooms: JSON.stringify(mappedRooms)
      });
    }

    const roomDisplayedOnMapPageIsUnavailableOnPDP = finalRooms.find(room => {
      // eslint-disable-next-line eqeqeq
      const rateMatches = get(room, 'rate.value') == priceValue;
      return rateMatches && room.availability === PropertyAvailability.SOLD_OUT;
    });

    if (roomDisplayedOnMapPageIsUnavailableOnPDP && !admin) {
      analytics.trackGA4('track-event-pricing', {
        ...analyticsObject,
        priceMismatchType: PriceMismatchEnums.PDPRoomUnavailability,
        rooms: JSON.stringify({
          id: roomDisplayedOnMapPageIsUnavailableOnPDP.id,
          pdpRateType: roomDisplayedOnMapPageIsUnavailableOnPDP?.nightlyRates?.[0]?.rateCategory,
          name: roomDisplayedOnMapPageIsUnavailableOnPDP.name,
          availability: roomDisplayedOnMapPageIsUnavailableOnPDP.availability,
          rate: get(roomDisplayedOnMapPageIsUnavailableOnPDP, 'rate.value')
        })
      });
    }

    const areAllRoomsSoldOut = roomAvailability.every(avail => avail === PropertyAvailability.SOLD_OUT);
    if (areAllRoomsSoldOut && !admin && priceValue != null) {
      const unavailableRooms = finalRooms
        .filter(room => room.availability === PropertyAvailability.SOLD_OUT)
        .map(room => ({
          id: room.id,
          name: room.name,
          pdpRateType: room?.nightlyRates?.[0]?.rateCategory,
          availability: room.availability,
          rate: get(room, 'rate.value')
        }));
      analytics.trackGA4('track-event-pricing', {
        ...analyticsObject,
        rooms: JSON.stringify(unavailableRooms),
        priceMismatchType: PriceMismatchEnums.SoldOutRooms
      });
    }
    window.sessionStorage.removeItem('propertyPricingMismatch');
  }, [auth, finalRooms, analytics, checkin, checkout, slug, propertyDetails.id]); // Use finalRooms and propertyDetails.id

  // Effect to run checkBookingPriceMismatch
  useEffect(() => {
    // Run only when rates are not loading and finalRooms have been processed
    if (!ratesLoading && finalRooms && finalRooms.length > 0) {
      checkBookingPriceMismatch();
    }
  }, [ratesLoading, finalRooms, checkBookingPriceMismatch]);

  // Determine overall loading/error state
  const isLoading = detailsLoading || (ratesLoading && !skipRatesQuery);
  const queryError = detailsError || ratesError;

  // Render props passed to children
  const renderProps = {
    checkin,
    checkout,
    currencyType: propertyDetails.currencyType,
    error: queryError,
    numRooms,
    loading: isLoading,
    ratesLoading: ratesLoading && !skipRatesQuery,
    nights,
    rooms: finalRooms,
    propertyConnected: propertyDetails.connected,
    bookingBypassReasonId,
    estimatedCost,
    capacity,
    totalRoomCount,
    initiateBooking: onInitiateBooking,
    changeDates: onDatesChanged,
    changeNumRooms: onNumRoomsChange,
    changeRoomCount: updateRoomCount,
    getRoomCount,
    roomInputs,
    isBlockBooking,
    showBlockBookingToggle,
    toggleBlockBooking
  };

  return children(renderProps);
};

// --- Outer Component with HOCs and Prop Types ---
function BookNowContainer(props) {
  // Render the internal component that uses hooks
  return <BookNowContainerInternal {...props} />;
}

BookNowContainer.propTypes = {
  // Props passed from parent / HOCs that remain
  analytics: withAnalyticsPropType.isRequired,
  booking: withBookingPropType.isRequired,
  children: PropTypes.func.isRequired,
  slug: PropTypes.string.isRequired,
  auth: withAuthPropType.isRequired,
  router: withRoutingPropType.isRequired,
  showError: PropTypes.func
};

BookNowContainer.defaultProps = {
  showError: () => {} // Provide default no-op
};

// Apply HOCs that provide necessary props (auth, booking, router, analytics)

export default withAuth()(withBooking()(withRouting(withAnalytics()(BookNowContainer))));
